'use client'

import { useTexasPropertyAuth, useTexasPropertyGetAll } from '@/hooks/data/texas-property'
import { TexasPropertySearchResult } from '@/types/texas-property'
import { Form, Select, Spin } from 'antd'
import axios, { HttpStatusCode } from 'axios'
import { useEffect, useRef, useState } from 'react'

export type SelectTexasPropertyProps = {
  formName: string
  formLabel: string
  onSelect?: (properties: TexasPropertySearchResult[]) => void
}

type SelectOption = {
  label: string
  value: number
}

export function SelectTexasProperty(props: SelectTexasPropertyProps) {
  const [searchQuery, setSearchQuery] = useState('travis')
  const [searchErr, setSearchErr] = useState<{
    isTimeout: boolean
  } | null>(null)

  // use a hardcoded year for now to ensure we get results
  // since we don't know if all properties will be available right away once the new year begins
  // eslint-disable-next-line prefer-const
  let year = '2026'

  const { isLoading: authLoading } = useTexasPropertyAuth()
  const { data, isLoading: isGetAllLoading, error, isError } = useTexasPropertyGetAll(searchQuery, year)

  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>([])

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  function onSearch(q: string) {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(q)
    }, 300)
  }

  function onSelect(ids: number[]) {
    const properties = data?.results.filter((p) => ids.includes(p.pid)) ?? []
    props.onSelect?.(properties)
  }

  function mapPropertiesToOption(property: TexasPropertySearchResult): SelectOption {
    let label = property.name + ' - ' + property.streetPrimary

    if (property.city) {
      label += `, ${property.city}`
    }

    label += ` (${property.pYear})`

    return {
      label,
      value: property.pid,
    }
  }

  useEffect(() => {
    if (data?.results) {
      let options: SelectOption[] = []

      if (year === 'all') {
        // filter options to only include the newest property for each pid, since there can be multiple years of data for the same property
        const pidToNewestProperty = new Map<number, TexasPropertySearchResult>()
        for (const property of data.results) {
          const existing = pidToNewestProperty.get(property.pid)
          if (!existing || Number(property.pYear) > Number(existing.pYear)) {
            pidToNewestProperty.set(property.pid, property)
          }

          options = Array.from(pidToNewestProperty.values()).map(mapPropertiesToOption)
        }
      } else {
        options = data.results.map(mapPropertiesToOption)
      }

      setFilteredOptions(options)
    }
  }, [data])

  useEffect(() => {
    if (!isError && data) {
      setSearchErr(null)
      return
    }

    if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.GatewayTimeout) {
      setSearchErr({ isTimeout: true })
    }
  }, [error, isError, data])

  return (
    <Form.Item required>
      <Form.Item
        label={props.formLabel}
        name={props.formName}
        rules={[
          {
            required: true,
            message: 'Please select your property',
          },
        ]}
        style={{
          marginBottom: -4,
        }}
      >
        <Select
          maxCount={4}
          options={filteredOptions}
          mode="multiple"
          showSearch
          onSearch={onSearch}
          onChange={onSelect}
          filterOption={false}
          placeholder="Search for property by address"
          getPopupContainer={(trigger) => trigger.parentElement!}
          notFoundContent={
            authLoading || isGetAllLoading ? (
              <div className="flex justify-center py-2">
                <Spin size="small" />
              </div>
            ) : undefined
          }
          dropdownRender={(menu) => (
            <>
              {authLoading || isGetAllLoading ? (
                <div className="flex justify-center py-2">
                  <Spin size="small" />
                </div>
              ) : (
                menu
              )}
            </>
          )}
        />
      </Form.Item>
      {searchErr && (
        <span className="text-red-500">
          {searchErr?.isTimeout
            ? 'Unspecific keyword!. Search property with a specific keyword, such as address or id.'
            : searchErr
              ? 'An error occurred while searching for properties. Please try again.'
              : ''}
        </span>
      )}
    </Form.Item>
  )
}
